/**
 * One Settings api-keys card, driven by a credential schema (§4.5): the
 * registry decides which services render and what config fields they carry.
 * Service-specific behavior (descriptions, feature gates, post-save side
 * effects) stays in SettingsPage's per-service UI map.
 */
import { useState, type ReactNode } from 'react'
import { Eye, EyeOff, Save, Trash2 } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
} from '@/components/ui'
import type { CredentialServiceSchema } from '@/types/credentialSchemas'

interface ApiKeyCardProps {
  schema: CredentialServiceSchema
  description?: string
  hasKey: boolean
  managed: boolean
  managedBanner: ReactNode
  badge: ReactNode
  saving: boolean
  clearing: boolean
  onSave: (key: string, config: Record<string, string>) => Promise<void>
  onClear: () => Promise<void>
}

export function ApiKeyCard({
  schema, description, hasKey, managed, managedBanner, badge,
  saving, clearing, onSave, onClear,
}: ApiKeyCardProps) {
  const [keyInput, setKeyInput] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [configInputs, setConfigInputs] = useState<Record<string, string>>({})

  const configFields = schema.fields.filter((f) => f.storage === 'config')
  const requiredConfigMissing = configFields.some(
    (f) => f.required && !(configInputs[f.key] ?? '').trim(),
  )

  const handleSave = async () => {
    const config: Record<string, string> = {}
    for (const f of configFields) {
      const v = (configInputs[f.key] ?? '').trim()
      if (v) config[f.key] = v
    }
    try {
      await onSave(keyInput.trim(), config)
      setKeyInput('')
      setConfigInputs({})
    } catch {
      // Save failed — the page already surfaces a toast; preserve what the
      // user typed so they don't have to re-enter a secret to retry.
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{schema.label} API Key</span>
          {badge}
        </CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {managed ? (
          managedBanner
        ) : (
          <>
            {configFields.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">API Key</label>
              </div>
            )}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showKey ? 'text' : 'password'}
                  placeholder={
                    hasKey ? '••••••••••••••••' : `Enter your ${schema.label} API key`
                  }
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                />
                <button
                  type="button"
                  aria-label={showKey ? 'Hide key' : 'Show key'}
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {configFields.length === 0 && (
                <SaveClearButtons
                  saving={saving} clearing={clearing} hasKey={hasKey}
                  disabled={!keyInput.trim() || requiredConfigMissing || saving}
                  onSave={handleSave} onClear={onClear}
                />
              )}
            </div>
            {configFields.map((f) => (
              <div className="space-y-2" key={f.key}>
                <label className="text-sm font-medium">{f.label}</label>
                <Input
                  placeholder={f.placeholder}
                  value={configInputs[f.key] ?? ''}
                  onChange={(e) =>
                    setConfigInputs((prev) => ({ ...prev, [f.key]: e.target.value }))
                  }
                />
                {f.help ? (
                  <p className="text-xs text-muted-foreground">{f.help}</p>
                ) : null}
              </div>
            ))}
            {configFields.length > 0 && (
              <div className="flex gap-2">
                <SaveClearButtons
                  saving={saving} clearing={clearing} hasKey={hasKey}
                  disabled={!keyInput.trim() || requiredConfigMissing || saving}
                  onSave={handleSave} onClear={onClear}
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function SaveClearButtons({
  saving, clearing, hasKey, disabled, onSave, onClear,
}: {
  saving: boolean; clearing: boolean; hasKey: boolean; disabled: boolean
  onSave: () => void; onClear: () => void
}) {
  return (
    <>
      <Button onClick={onSave} disabled={disabled} isLoading={saving}>
        <Save className="w-4 h-4 mr-2" />
        Save
      </Button>
      {hasKey && (
        <Button variant="destructive" onClick={onClear} disabled={clearing} isLoading={clearing} aria-label="Clear key">
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </>
  )
}
